import { useSelector } from 'react-redux';
import { getAppUser } from '../../../app/appSelectors';
import IconUserStatus from '../IconUserStatus';
import {
    UserStatusCircleProgressLockStyled,
    UserStatusCircleProgressSkeletonStyled,
    UserStatusCircleProgressStyled,
} from './styled/UserStatusCircleProgressStyled';
import { TUserStatuses, TUserStatusesStates } from '../UserStatuses.t';
import { USER_STATUSES_CONFIG, USER_STATUSES_STATES } from '../userStatusesConstants';
import BinIcon from '../../../components/icon/IconWrapped';
import classNames from 'classnames';
import UserStatusCircleProgressGlowingBackground from './UserStatusCircleProgressGlowingBackground';
import useUserStatusCircleProgressAnimation from './hooks/useUserStatusCircleProgressAnimation';

interface IUserStatusCircleProgressProps {
    isShowUpdatedProgressAnimation?: boolean,
    progress?: number,
    radius?: number,
    showSkeleton?: boolean,
    size?: number,
    state?: TUserStatusesStates,
    userStatus?: TUserStatuses,
    width?: number,
}

function UserStatusCircleProgress(props: IUserStatusCircleProgressProps) {
    const user = useSelector(getAppUser);
    const {
        isShowUpdatedProgressAnimation,
        progress,
        radius = props.size / 2 || 20,
        showSkeleton,
        state,
        userStatus = user.user_status,
        width = 3,
    } = props;
    const glowingBackgroundColor = USER_STATUSES_CONFIG[userStatus]?.glowingBackgroundColorDark;
    const { currentAnimationStageConfig, progressToShow } = useUserStatusCircleProgressAnimation({
        currentProgress: progress,
        isShowAnimation: isShowUpdatedProgressAnimation,
    });
    const circleProgressSize = radius * 2 + width;
    const glowingBackgroundSize = circleProgressSize * 3.6;

    if (!userStatus) {
        return null;
    }

    if (showSkeleton) {
        return <UserStatusCircleProgressSkeletonStyled width={width} radius={radius} className={'wave-background'}/>;
    }

    return <div className={'position-relative'}>
        {isShowUpdatedProgressAnimation && !!glowingBackgroundColor && <UserStatusCircleProgressGlowingBackground
            animationDurationMs={currentAnimationStageConfig.animationDurationMs}
            animationType={currentAnimationStageConfig.animationType}
            color={glowingBackgroundColor}
            size={glowingBackgroundSize}
            isShow={currentAnimationStageConfig.showGlowingBackground}
        />}
        <UserStatusCircleProgressStyled
            animationDurationMs={currentAnimationStageConfig.animationDurationMs}
            animationType={currentAnimationStageConfig.animationType}
            strokeColor={USER_STATUSES_CONFIG[userStatus].progressColor}
            className={classNames({ 'state-completed': state === USER_STATUSES_STATES.COMPLETED })}
            progress={progressToShow}
            scale={currentAnimationStageConfig.progressBarSizeMultiplier}
            radius={radius}
            width={width}
        >
            {
                typeof progressToShow === 'number' && <div className="percent">
                    <svg>
                        <circle cx={radius} cy={radius} r={radius}></circle>
                        <circle className={classNames({ hidden: !progressToShow })} cx={radius} cy={radius} r={radius}></circle>
                    </svg>
                </div>
            }
            <div className="icon">
                <IconUserStatus name={userStatus} size={radius * 1.2} state={state}/>
            </div>
            {
                state === USER_STATUSES_STATES.PLAN && <UserStatusCircleProgressLockStyled>
                    <BinIcon name={'IcLock'} size={16}/>
                </UserStatusCircleProgressLockStyled>
            }
        </UserStatusCircleProgressStyled>
    </div>;
}

export default UserStatusCircleProgress;
